<script setup lang="ts">
  import { computed, ref, useTemplateRef, type Component } from 'vue';
  import { NotebookPen } from 'lucide-vue-next';
  import type { QualityRecordRow } from '../../model/quality-record-row.types';
  import { RECORD_TYPE_UPLOAD_NAME } from '@/entities/GeneratedRecord';
  import { usePermission } from '@/entities/Permission';
  import { ActionMenu, IconButton, IconImage, Tooltip } from '@/shared/ui';
  import type { ActionMenuItem } from '@/shared/ui';
  import EyeIcon from '@/shared/assets/icons/EyeActionIcon.svg';
  import PencilIcon from '@/shared/assets/icons/PencilBlack.svg';
  import RenameIcon from '@/shared/assets/icons/TextBoxIcon.svg';
  import LinkIcon from '@/shared/assets/icons/LinkIcon.svg';
  import EllipsisIcon from '@/shared/assets/icons/EllipsisIcon.svg';
  import TrashIcon from '@/shared/assets/icons/RedTrashIcon.svg';
  import FillLogsIcon from '@/shared/assets/icons/FillLogsIcon.svg';
  import UploadActionIcon from '@/shared/assets/icons/UploadActionIcon.svg';
  import PdfFileIcon from '@/shared/assets/icons/PdfFileIcon.svg';
  import { useEditUploadRecord } from '../../model/useEditUploadRecord';
  import { useFillGeneratedRecord } from '../../model/useFillGeneratedRecord';
  import { useGeneratedRecordLink } from '../../model/useGeneratedRecordLink';
  import { useOpenFillLogs } from '../../model/useOpenFillLogs';
  import { useRecordSheetExport } from '../../model/useRecordSheetExport';
  import { useRowMenuPosition } from '../../model/useRowMenuPosition';
  import RecordSheetExportStage from './RecordSheetExportStage.vue';
  import UploadRecordPdfModal from './UploadRecordPdfModal.vue';

  const props = defineProps<{
    revision: QualityRecordRow;
  }>();

  const emit = defineEmits<{
    refresh: [];
  }>();

  const { can } = usePermission();
  const canUpdateRecord = can('update', 'Record');
  const { fillGeneratedRecord } = useFillGeneratedRecord();
  const { openFillLogs } = useOpenFillLogs();
  const { editUploadRecord } = useEditUploadRecord();
  const { viewGeneratedRecord, copyGeneratedRecordLink } = useGeneratedRecordLink();
  const sheetStage = useTemplateRef<InstanceType<typeof RecordSheetExportStage>>('sheetStage');
  const { isExporting, sheetForm, chunkEdges, exportRecordSheet } = useRecordSheetExport(
    () => sheetStage.value?.element ?? null,
    () => emit('refresh')
  );

  const isUpload = computed(() => props.revision.recordTypeName === RECORD_TYPE_UPLOAD_NAME);
  const isUploadModalOpen = ref(false);

  const editRecord = async () => {
    if ((await fillGeneratedRecord(props.revision.id)) === 'stale') emit('refresh');
  };

  const notImplemented = () => {};

  interface RowIconAction {
    key: string;
    label: string;
    icon: string | Component;
    iconSize?: number;
    onClick: () => unknown;
    disabled: boolean;
    disabledHint?: string;
    loading?: boolean;
    requiresUpdate?: boolean;
  }

  const EXPORTING_HINT = 'Gerando PDF...';

  const UPLOAD_EDIT_CLOSED_HINT = 'Este Registro já recebeu um PDF e não pode mais ser editado.';

  // An Upload record is never opened for filling: its answers arrive as PDFs, and its sheet is
  // edited only until the first one. "Editar" stays in place, disabled with a hint, because
  // hiding it shifted every icon after it out of the column.
  const fillActions = computed<RowIconAction[]>(() => {
    if (!isUpload.value) {
      return [
        {
          key: 'fill',
          label: 'Preencher',
          icon: NotebookPen,
          iconSize: 20,
          onClick: editRecord,
          disabled: false,
          requiresUpdate: true,
        },
      ];
    }
    return [
      {
        key: 'send-pdf',
        label: 'Enviar PDF',
        icon: UploadActionIcon,
        onClick: () => {
          isUploadModalOpen.value = true;
        },
        disabled: false,
        requiresUpdate: true,
      },
      {
        key: 'export-pdf',
        label: 'Exportar PDF',
        icon: PdfFileIcon,
        onClick: () => exportRecordSheet(props.revision.id),
        disabled: false,
        loading: isExporting.value,
      },
      {
        key: 'edit',
        label: 'Editar',
        icon: PencilIcon,
        onClick: () => editUploadRecord(props.revision.id),
        disabled: props.revision.hasPdfResponse,
        disabledHint: props.revision.hasPdfResponse ? UPLOAD_EDIT_CLOSED_HINT : undefined,
        requiresUpdate: true,
      },
    ];
  });

  const iconActions = computed<RowIconAction[]>(() =>
    [
      {
        key: 'view',
        label: 'Visualizar',
        icon: EyeIcon,
        onClick: () => viewGeneratedRecord(props.revision.id),
        disabled: false,
      },
      ...fillActions.value,
      {
        key: 'rename',
        label: 'Renomear',
        icon: RenameIcon,
        onClick: notImplemented,
        disabled: true,
        requiresUpdate: true,
      },
      {
        key: 'copy-link',
        label: 'Copiar link',
        icon: LinkIcon,
        onClick: () => copyGeneratedRecordLink(props.revision.id),
        disabled: false,
      },
    ].filter((action) => !action.requiresUpdate || canUpdateRecord.value)
  );

  const GENERATED_RECORD_MENU_ITEMS: ActionMenuItem[] = [
    {
      id: 'fill-logs',
      label: 'Logs de preenchimento',
      icon: FillLogsIcon,
      onClick: () => openFillLogs(props.revision.id),
    },
    { id: 'delete', label: 'Deletar', icon: TrashIcon, onClick: notImplemented, disabled: true },
  ];

  const MENU_CLASS =
    'fixed top-[var(--row-menu-top)] left-[var(--row-menu-left)] z-50 w-56 rounded-2xl border ' +
    'border-gray-02 bg-white-01 p-2 shadow-xl';

  const isMenuOpen = ref(false);
  const { triggerRef, menuStyle } = useRowMenuPosition(isMenuOpen, {
    width: 224,
    height: 112,
  });
</script>

<template>
  <div class="flex gap-2 items-center text-gray-xx-02">
    <template
      v-for="action in iconActions"
      :key="action.key"
    >
      <Tooltip
        v-if="action.disabled"
        :content="action.disabledHint ?? action.label"
        placement="top"
        class="inline-flex"
        focusable
      >
        <span class="inline-flex pointer-events-none">
          <IconButton
            :src="action.icon"
            :alt="action.label"
            :size="action.iconSize"
            disabled
          />
        </span>
      </Tooltip>
      <Tooltip
        v-else
        :content="action.loading ? EXPORTING_HINT : action.label"
        placement="top"
        class="inline-flex"
      >
        <IconButton
          :src="action.icon"
          :alt="action.label"
          :loading="action.loading"
          :size="action.iconSize"
          @click="action.onClick"
        />
      </Tooltip>
    </template>

    <div
      ref="triggerRef"
      class="flex items-center"
      :style="menuStyle"
    >
      <ActionMenu
        v-model="isMenuOpen"
        :items="GENERATED_RECORD_MENU_ITEMS"
        stop-trigger-click-propagation
        :menu-class="MENU_CLASS"
      >
        <template #trigger="{ isOpen }">
          <Tooltip
            content="Mais ações"
            placement="top"
            class="flex"
          >
            <button
              type="button"
              class="inline-flex size-6 shrink-0 items-center justify-center text-gray-xx-02"
              aria-label="Mais ações"
              aria-haspopup="menu"
              :aria-expanded="isOpen"
            >
              <IconImage
                :src="EllipsisIcon"
                alt=""
              />
            </button>
          </Tooltip>
        </template>
      </ActionMenu>
    </div>

    <UploadRecordPdfModal
      v-if="isUploadModalOpen"
      :record-id="revision.id"
      :has-pdf-response="revision.hasPdfResponse"
      @close="isUploadModalOpen = false"
      @refresh="emit('refresh')"
    />

    <RecordSheetExportStage
      v-if="sheetForm"
      ref="sheetStage"
      :form="sheetForm"
      :chunk-edges="chunkEdges"
    />
  </div>
</template>
